import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from "../api/auth";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const savedUserStr = localStorage.getItem('user');
        const savedToken = localStorage.getItem('access_token');

        // If we have both a token and user data, restore session
        if (savedToken && savedUserStr) {
          setUser(JSON.parse(savedUserStr));
          setLoading(false);
          return;
        }

        // If token exists but no user data, clear everything
        if (savedToken && !savedUserStr) {
          localStorage.removeItem('access_token');
        }

        // Try Telegram auto-login using the signed initData (HMAC-verified server-side)
        const telegramAuthDisabled = localStorage.getItem('telegram_auth_disabled') === 'true';
        const telegramInitData = localStorage.getItem('telegram_init_data');

        if (!telegramAuthDisabled && telegramInitData) {
          try {
            const response = await authAPI.loginWithTelegram(telegramInitData);

            if (response.success && response.data) {
              // Store token and user data
              if (response.data.access_token) {
                localStorage.setItem('access_token', response.data.access_token);
              }
              const { access_token, token_type, ...userData } = response.data;
              setUser(userData);
              localStorage.setItem('user', JSON.stringify(userData));
              setLoading(false);
              return;
            }
          } catch (e) {
            // Telegram auth failed, continue to manual login
          }
        }

        // Restore from localStorage if available
        if (savedUserStr) {
          setUser(JSON.parse(savedUserStr));
        }

      } catch (error) {
        console.error('Auth init error:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('access_token');
      } finally {
        setLoading(false);
      }
    };

    initAuth().catch(console.error);
  }, []);

  const login = (userData) => {
    // Extract and store JWT token separately
    if (userData.access_token) {
      localStorage.setItem('access_token', userData.access_token);
      const { access_token, token_type, ...userInfo } = userData;
      setUser(userInfo);
      localStorage.setItem('user', JSON.stringify(userInfo));
    } else {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('access_token');
    // Clear Telegram auth data
    localStorage.removeItem('telegram_user');
    localStorage.removeItem('pending_telegram_user');
    localStorage.removeItem('telegram_id_for_registration');
    localStorage.setItem('telegram_auth_disabled', 'true');
    // Clear avatar (personal data)
    if (user?.id) {
      localStorage.removeItem(`avatar_${user.id}`);
    }
  };

  const deleteAccount = async () => {
    try {
      const { default: axios } = await import('../api/axios');
      await axios.delete('/auth/account');
      logout();
      return { success: true };
    } catch (error) {
      console.error('Account deletion error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to delete account'
      };
    }
  };

  const updateUser = (newData) => {
    const updatedUser = { ...user, ...newData };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value = {
    user,
    login,
    logout,
    updateUser,
    deleteAccount,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
